import mealBowlImageUrl from "@/assets/meal-bowl.svg"
import measurementDropImageUrl from "@/assets/measurement-drop-2.svg"
import quickCheckImageUrl from "@/assets/quick-check.svg"
import sunImageUrl from "@/assets/sun.svg"
import Spinner from "@/components/Spinner"
import { Button } from "@/components/chadcn/Button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/chadcn/Dialog"
import { useObservationByIdQuery } from "@/data/queries/measurementQueries"
import { ROUTES, buildPath } from "@/routes"
import { DateTimeFormatter } from "@js-joda/core"
import { Locale } from "@js-joda/locale"
import { ChevronRightIcon } from "@radix-ui/react-icons"
import { useNavigate, useSearchParams } from "react-router"
import type { DashboardTab } from "../dashboardData"
import { isDashboardTab } from "../dashboardData"

type NewMeasurementModalProps = {
  // When set the modal renders in contextualization mode: the patient just
  // followed the BG-meter SMS deeplink and we display the reading value plus
  // the three context-picker buttons. When absent the modal is the FAB-driven
  // "record a reading" picker (no reading attached, manual flow).
  measurementId?: string
  onClose: () => void
}

const RECEIVED_AT_FORMATTER = DateTimeFormatter.ofPattern("dd MMM - h:mm a").withLocale(Locale.US)

const MODAL_CONTENT_CLASSES =
  "w-[calc(100vw-32px)] max-w-[440px] rounded-[22px] border-0 bg-white p-6 pt-14 shadow-lg overflow-visible"

const ModalShell = ({ children }: { children: React.ReactNode }) => (
  <DialogContent className={MODAL_CONTENT_CLASSES} aria-describedby={undefined}>
    <div className="absolute -top-10 left-1/2 flex h-20 w-20 -translate-x-1/2 items-center justify-center rounded-full bg-white">
      <img alt="" aria-hidden src={measurementDropImageUrl} width={40} />
    </div>
    <div className="min-h-[260px]">{children}</div>
  </DialogContent>
)

const NewMeasurementModal = ({ measurementId, onClose }: NewMeasurementModalProps) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const tabParam = searchParams.get("tab")
  const activeTab: DashboardTab = isDashboardTab(tabParam) ? tabParam : "overview"

  const handleManualNavigate = (route: string) => {
    onClose()
    navigate(route)
  }

  const handleContextualizeNavigate = (route: string) => {
    onClose()
    navigate(route, { state: { readingId: measurementId, initialTab: activeTab } })
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <ModalShell>
        {measurementId ? (
          <ContextualizeBody
            measurementId={measurementId}
            activeTab={activeTab}
            onPickContext={handleContextualizeNavigate}
            onClose={onClose}
          />
        ) : (
          <ManualBody onPickContext={handleManualNavigate} />
        )}
      </ModalShell>
    </Dialog>
  )
}

type ContextualizeBodyProps = {
  measurementId: string
  activeTab: DashboardTab
  onPickContext: (route: string) => void
  onClose: () => void
}

const ContextualizeBody = ({
  measurementId,
  activeTab,
  onPickContext,
  onClose,
}: ContextualizeBodyProps) => {
  const navigate = useNavigate()
  const { data: observation, isPending } = useObservationByIdQuery({ readingId: measurementId })

  if (isPending) {
    return (
      <>
        <DialogTitle className="sr-only">Loading reading</DialogTitle>
        <div className="flex h-[260px] items-center justify-center">
          <Spinner />
        </div>
      </>
    )
  }

  if (!observation || !observation.reading) {
    return (
      <>
        <DialogHeader>
          <DialogTitle className="text-center">No reading found</DialogTitle>
        </DialogHeader>
        <div className="mt-6">
          <Button className="w-full" onClick={onClose}>
            Ok
          </Button>
        </div>
      </>
    )
  }

  const reading = observation.reading
  const receivedAt = observation.dateTime.format(RECEIVED_AT_FORMATTER)

  if (observation.form) {
    // Already contextualized — funnel through to edit instead of re-picking.
    const handleEdit = () => {
      onClose()
      navigate(
        buildPath(
          observation.form?.context === "quick-check"
            ? ROUTES.QUICK_CHECK_EDIT
            : ROUTES.MEASUREMENT.EDIT,
          { measurementId },
        ),
        { state: { alreadyContextualized: true, initialTab: activeTab } },
      )
    }
    return (
      <>
        <DialogTitle className="sr-only">Edit reading</DialogTitle>
        <ReadingHeader
          glucose={reading.bloodGlucoseInMilligramsPerDeciliter}
          receivedAt={receivedAt}
        />
        <p className="mt-6 text-center text-sm text-primary-contrast">
          You already provided information for this measurement. You can update it by clicking the
          edit button below.
        </p>
        <div className="mt-6 px-4">
          <Button className="w-full" onClick={handleEdit}>
            Edit
          </Button>
        </div>
      </>
    )
  }

  return (
    <>
      <DialogTitle className="sr-only">Add context for your reading</DialogTitle>
      <ReadingHeader
        glucose={reading.bloodGlucoseInMilligramsPerDeciliter}
        receivedAt={receivedAt}
      />
      <div className="mt-6 flex flex-col gap-4">
        <ContextButton
          icon={sunImageUrl}
          label="Fasting"
          onClick={() => onPickContext(ROUTES.MEASUREMENT.FASTING)}
        />
        <ContextButton
          icon={mealBowlImageUrl}
          label="After meal"
          onClick={() => onPickContext(ROUTES.MEASUREMENT.MEAL)}
        />
        <ContextButton
          icon={quickCheckImageUrl}
          label="Quick check"
          onClick={() => onPickContext(ROUTES.MEASUREMENT.QUICK_CHECK)}
        />
      </div>
    </>
  )
}

const ReadingHeader = ({ glucose, receivedAt }: { glucose: number; receivedAt: string }) => (
  <>
    <div className="flex justify-center">
      <div className="relative">
        <span className="text-[80px] font-bold leading-none text-primary-contrast">{glucose}</span>
        <span className="absolute -right-11 bottom-3 text-sm text-primary-contrast">mg/dL</span>
      </div>
    </div>
    <p className="mt-3 rounded-[10px] bg-[#F2F2F2] p-3 text-center text-h3 font-medium text-primary-contrast">
      Received {receivedAt}
    </p>
  </>
)

type ManualBodyProps = {
  onPickContext: (route: string) => void
}

const ManualBody = ({ onPickContext }: ManualBodyProps) => (
  <>
    <DialogTitle className="text-center text-h2">Hi! What would you like to do?</DialogTitle>
    <div className="mt-6 flex flex-col gap-4">
      <ContextButton label="View Logs" onClick={() => onPickContext(ROUTES.DASHBOARD)} />
      <ContextButton
        label="Log a meal or snack"
        onClick={() => onPickContext(ROUTES.ADD_FOOD_LOG)}
      />
    </div>
  </>
)

type ContextButtonProps = {
  label: string
  onClick: () => void
  icon?: string
}

const ContextButton = ({ label, onClick, icon }: ContextButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center gap-4 rounded-[12px] border-4 border-primary bg-white p-4 text-left text-h4 text-primary-contrast shadow-card hover:bg-secondary"
  >
    {icon && <img alt="" aria-hidden src={icon} className="h-8 w-8" />}
    <span className="flex-1 text-left">{label}</span>
    <ChevronRightIcon aria-hidden className="h-5 w-5" />
  </button>
)

export default NewMeasurementModal
